import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { apiClient } from '../utils/api';
import { pgApi } from '../utils/pgApi';
import ErrorMessage from '../components/common/ErrorMessage';
import { useToast } from '../components/common/ToastProvider';
import { useAuth } from '../auth/AuthContext';
import '../styles/dashboard.css'; // Ensure styles are available

export const UserPreferences = () => {
    const { hasPermission, user: currentUser } = useAuth();
    const canListAll = hasPermission('users:read');

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [preferences, setPreferences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentPreferenceId, setCurrentPreferenceId] = useState(null);
    const [formData, setFormData] = useState({
        user_id: '',
        category: '',
        preference_name: '',
        buudii_score: '',
        url: '',
        icon: '',
        metadata: '{}'
    });
    const [submitting, setSubmitting] = useState(false);

    const { addToast } = useToast();

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                if (canListAll) {
                    const usersData = await apiClient.getUsers();
                    setUsers(usersData);
                } else {
                    // Fetch only self
                    const myUser = await pgApi.getUser(currentUser.id);
                    setUsers([myUser]);
                    setSelectedUser(String(myUser.id));
                }
            } catch (err) {
                console.error('Failed to fetch users:', err);
                addToast({ type: 'error', title: 'Error', description: 'Failed to load users' });
            }
        };
        fetchUsers();
    }, [addToast, canListAll, currentUser]);

    const fetchPreferences = React.useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiClient.getUserPreferences(selectedUser);
            setPreferences(data);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load user preferences');
        } finally {
            setLoading(false);
        }
    }, [selectedUser]);

    useEffect(() => {
        fetchPreferences();
    }, [fetchPreferences]);

    const handleOpenCreateModal = () => {
        setFormData({
            user_id: selectedUser || '',
            category: '',
            preference_name: '',
            buudii_score: '',
            url: '',
            icon: '',
            metadata: '{}'
        });
        setIsEditing(false);
        setCurrentPreferenceId(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (pref) => {
        setFormData({
            user_id: pref.user_id,
            category: pref.category,
            preference_name: pref.preference_name,
            buudii_score: pref.buudii_score,
            url: pref.url,
            icon: pref.icon || '',
            metadata: JSON.stringify(pref.metadata || {}, null, 2)
        });
        setIsEditing(true);
        setCurrentPreferenceId(pref.id);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSubmitting(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const payload = {
                ...formData,
                user_id: parseInt(formData.user_id, 10),
                metadata: JSON.parse(formData.metadata || '{}')
            };

            if (isEditing) {
                await apiClient.updateUserPreference(currentPreferenceId, payload);
                addToast({ type: 'success', title: 'Success', description: 'Preference updated successfully' });
            } else {
                await apiClient.createUserPreference(payload);
                addToast({ type: 'success', title: 'Success', description: 'Preference created successfully' });
            }

            handleCloseModal();
            fetchPreferences();
        } catch (err) {
            console.error('Failed to save preference:', err);
            addToast({ type: 'error', title: 'Error', description: err.message || 'Failed to save preference' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this preference?')) {
            return;
        }

        try {
            await apiClient.deleteUserPreference(id);
            addToast({ type: 'success', title: 'Success', description: 'Preference deleted successfully' });
            fetchPreferences();
        } catch (err) {
            console.error('Failed to delete preference:', err);
            addToast({ type: 'error', title: 'Error', description: err.message || 'Failed to delete preference' });
        }
    };

    const handleUserChange = (e) => {
        setSelectedUser(e.target.value);
    };

    if (loading && !preferences.length) {
        return (
            <div className="dashboard-content">
                <div className="loading-message">
                    <p>Loading user preferences...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-content">
                <ErrorMessage title="Error" message={error} />
            </div>
        );
    }

    return (
        <div className="dashboard-content">
            <header style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2>User Preferences</h2>
                    <p className="section-intro">
                        View and manage user preferences.
                    </p>
                </div>
                <button className="primary-btn" onClick={handleOpenCreateModal}>
                    Add Preference
                </button>
            </header>

            <div className="data-controls" style={{ marginBottom: '20px' }}>
                <div className="data-controls__layout">
                    <div className="device-dropdown">
                        <label htmlFor="user-select" style={{ marginRight: '10px' }}>USER:</label>
                        <select
                            id="user-select"
                            value={selectedUser}
                            onChange={handleUserChange}
                            style={{ padding: '8px', borderRadius: '4px' }}
                            disabled={!canListAll}
                        >
                            {canListAll && <option value="">All Users</option>}
                            {users.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.firstname} {user.lastname} ({user.email})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="data-section">
                <div className="plot-section">
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--color-text)' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.12)', textAlign: 'left' }}>
                                    <th style={{ padding: '1rem', textTransform: 'uppercase', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>ID</th>
                                    <th style={{ padding: '1rem', textTransform: 'uppercase', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>User ID</th>
                                    <th style={{ padding: '1rem', textTransform: 'uppercase', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>Category</th>
                                    <th style={{ padding: '1rem', textTransform: 'uppercase', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>Name</th>
                                    <th style={{ padding: '1rem', textTransform: 'uppercase', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>Score</th>
                                    <th style={{ padding: '1rem', textTransform: 'uppercase', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>URL</th>
                                    <th style={{ padding: '1rem', textTransform: 'uppercase', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preferences.map((pref) => (
                                    <tr key={pref.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                        <td style={{ padding: '1rem' }}>{pref.id}</td>
                                        <td style={{ padding: '1rem' }}>{pref.user_id}</td>
                                        <td style={{ padding: '1rem' }}>{pref.category}</td>
                                        <td style={{ padding: '1rem' }}>{pref.preference_name}</td>
                                        <td style={{ padding: '1rem' }}>{pref.buudii_score}</td>
                                        <td style={{ padding: '1rem' }}>
                                            {pref.url && (
                                                <a
                                                    href={pref.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
                                                >
                                                    {pref.url}
                                                </a>
                                            )}
                                        </td>
                                        <td style={{ padding: '1rem', display: 'flex', gap: '0.5rem' }}>
                                            <button
                                                className="secondary-btn small-btn"
                                                onClick={() => handleOpenEditModal(pref)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="danger-btn small-btn"
                                                onClick={() => handleDelete(pref.id)}
                                                style={{ backgroundColor: 'rgba(255, 0, 0, 0.2)', color: '#ff6b6b', border: '1px solid #ff6b6b' }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {preferences.length === 0 && (
                                    <tr>
                                        <td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                                            No user preferences found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {isModalOpen && ReactDOM.createPortal(
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal__header">
                            <h3>{isEditing ? 'Edit Preference' : 'Add Preference'}</h3>
                            <button className="modal__close" onClick={handleCloseModal}>&times;</button>
                        </div>
                        <div className="modal__body">
                            <form onSubmit={handleSubmit} id="preference-form">
                                <div className="form-field">
                                    <label className="form-label">User ID</label>
                                    <input
                                        type="number"
                                        name="user_id"
                                        value={formData.user_id}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Category</label>
                                    <input
                                        type="text"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Preference Name</label>
                                    <input
                                        type="text"
                                        name="preference_name"
                                        value={formData.preference_name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Buudii Score</label>
                                    <input
                                        type="text"
                                        name="buudii_score"
                                        value={formData.buudii_score}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">URL</label>
                                    <input
                                        type="text"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Icon</label>
                                    <input
                                        type="text"
                                        name="icon"
                                        value={formData.icon}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Metadata (JSON)</label>
                                    <textarea
                                        name="metadata"
                                        value={formData.metadata}
                                        onChange={handleInputChange}
                                        rows="3"
                                        style={{ fontFamily: 'monospace' }}
                                    />
                                </div>
                            </form>
                        </div>
                        <div className="modal__footer">
                            <button type="button" className="secondary-btn" onClick={handleCloseModal}>
                                Cancel
                            </button>
                            <button type="submit" form="preference-form" className="primary-btn" disabled={submitting}>
                                {submitting ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
